import type { ConditionSymbols } from '#src/observation-logic/conditionParser.ts'
import type {
  Condition,
  ConditionLiteral,
  Operand,
} from '#src/observation-logic/conditionSchema.ts'

const OR = 1
const AND = 2
const NOT = 3
const ATOM = 4
const BARE = /^[A-Za-z_][A-Za-z0-9_.-]*$/

// The condition in the language `parseCondition` reads, so that
// `parseCondition(printCondition(c))` gives back `c`. Observation operands
// print by their name in `symbols` when one names the same code and system.
// An empty group has no spelling and prints as an empty string.
export function printCondition(
  condition: Condition,
  symbols: ConditionSymbols = {}
): string {
  return print(condition, 0, symbols)
}

function print(
  condition: Condition,
  parent: number,
  symbols: ConditionSymbols
): string {
  switch (condition.type) {
    case 'all':
    case 'any': {
      const precedence = condition.type === 'all' ? AND : OR
      const parts = condition.conditions.map((child) =>
        print(child, precedence, symbols)
      )
      if (parts.length === 1) {
        return print(condition.conditions[0], parent, symbols)
      }
      const joined = parts.join(condition.type === 'all' ? ' and ' : ' or ')
      return parent > precedence ? `(${joined})` : joined
    }
    case 'not': {
      const inner = `not ${print(condition.condition, NOT, symbols)}`
      return parent > NOT ? `(${inner})` : inner
    }
    case 'answered':
      return `${printOperand(condition.operand, symbols)} is ${condition.answered ? '' : 'not '}answered`
    case 'compare': {
      const operand = printOperand(condition.operand, symbols)
      if (Array.isArray(condition.value)) {
        return `${operand} in [${condition.value.map(printLiteral).join(', ')}]`
      }
      const text = `${operand} ${condition.op} ${printLiteral(condition.value)}`
      return parent > ATOM ? `(${text})` : text
    }
  }
}

function printOperand(operand: Operand, symbols: ConditionSymbols): string {
  if (operand.source === 'variable') {
    return operand.id
  }
  const name =
    Object.entries(symbols).find(
      ([, symbol]) =>
        symbol.code === operand.code &&
        (symbol.system ?? undefined) === (operand.system ?? undefined)
    )?.[0] ?? operand.code
  return `#${name}${operand.scope ? `@${operand.scope}` : ''}`
}

export function printLiteral(literal: ConditionLiteral): string {
  switch (literal.kind) {
    case 'number':
      return literal.unit
        ? `${literal.value} ${literal.unit}`
        : `${literal.value}`
    case 'date':
      return literal.value
    case 'string':
      return JSON.stringify(literal.value)
    case 'boolean':
      return literal.value ? 'true' : 'false'
    case 'coding':
      return BARE.test(literal.code)
        ? literal.code
        : JSON.stringify(literal.code)
  }
}
